import classNames from 'classnames'
import React, { ReactNode, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'

import { CloseIcon } from '../../icon/icon.js'

// Render the shared close (X) button in the modal's top-right corner. Opt-in
// so modals own their own header layout, but the button itself is uniform.
// A shown button always needs its translated accessible name from the caller.
type BaseModalCloseProps =
    | { showClose: true; closeAriaLabel: string }
    | { showClose?: false; closeAriaLabel?: undefined }

export type BaseModalProps = BaseModalCloseProps & {
    isOpen: boolean
    onClose: () => void
    children: ReactNode
    className?: string
    backdropClassName?: string
    disableBackdropClose?: boolean
    outsideContent?: ReactNode
    /** Accessible name of the dialog (screen readers announce it on open). */
    ariaLabel?: string
}

export const BaseModal: React.FC<BaseModalProps> = ({
    isOpen,
    onClose,
    children,
    className,
    backdropClassName,
    disableBackdropClose,
    outsideContent,
    showClose,
    closeAriaLabel,
    ariaLabel,
}) => {
    const modalRef = useRef<HTMLDivElement | null>(null)

    // ESC key
    useEffect(() => {
        if (!isOpen) return

        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }

        document.addEventListener('keydown', handleKey)
        document.body.style.overflow = 'hidden'

        return () => {
            document.removeEventListener('keydown', handleKey)
            document.body.style.overflow = ''
        }
    }, [isOpen, onClose])

    if (!isOpen) return null

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (disableBackdropClose) return
        // Close only on a click that lands on the backdrop itself — not on the modal
        // content or on portaled children (e.g. the date-picker popover) whose events
        // still bubble through the React tree even though they render to document.body.
        if (e.target === e.currentTarget) {
            onClose()
        }
    }

    return ReactDOM.createPortal(
        <div
            className={classNames('modal__backdrop', backdropClassName)}
            onClick={handleBackdropClick}
        >
            <div
                ref={modalRef}
                role="dialog"
                aria-modal="true"
                aria-label={ariaLabel}
                className={classNames('modal__container', className)}
            >
                {showClose && (
                    <button
                        type="button"
                        className="modal__close"
                        onClick={onClose}
                        aria-label={closeAriaLabel}
                    >
                        <CloseIcon size={18} />
                    </button>
                )}
                {children}
            </div>
            {outsideContent}
        </div>,
        document.body,
    )
}
